import { useQuery } from "@tanstack/react-query";
import {
  DSC_STAMP_DETAILS,
  DSC_STAMP_PLACEMENT,
  DSC_TOKENS,
  DscStampPlacement,
  DscToken,
  SignableDocument,
} from "../config/levelThreeSigning";
import { _axios } from "./axios";

/**
 * The look of the DSC stamp, as kept on the server in
 * civic-plan-backend/config/dsc-stamp.json. Every value is optional: one that
 * is missing is not sent, and the signer program then uses its own
 * config.json for it.
 */
export type DscStampStyle = {
  heading?: string;
  showTick?: boolean;
  tickSize?: number;
  fontSize?: number;
  borderWidth?: number;
  timestampFormat?: string;
  /** "right" or "centre": how the stamp lines up with the anchor text. */
  anchorAlign?: string;
  /** Letter size of the heading; when missing it follows the stamp's height. */
  headingSize?: number;
  /** "right" ends the lines at the stamp's right edge, under the anchor's last letter. */
  textAlign?: string;
  /** One line of the stamp each, e.g. "Reason: %(reason)s". */
  lines?: string[];
  reason?: string;
  location?: string;
  projectCode?: string;
};

export type DscStampConfig = {
  stamp?: DscStampStyle;
  documents?: Partial<Record<SignableDocument, Partial<DscStampPlacement>>>;
  tokens?: DscToken[];
};

/**
 * The server's stamp settings and token list. Read fresh each time the
 * officer opens Apply DSC, so a change to the server file is used straight
 * away. While it loads or when it fails, the built-in values below are used.
 */
export function useDscStampConfig(enabled: boolean) {
  return useQuery({
    queryKey: ["dsc-stamp-config"],
    enabled,
    staleTime: 0,
    retry: false,
    queryFn: () =>
      _axios.get<{ success: true; data?: DscStampConfig }>(
        "/dsc-signing-log/stamp-config"
      ),
    select: (response) => response.data?.data ?? {},
  });
}

/** The token list: the server's when it has one, otherwise the built-in list. */
export function dscTokensFrom(config?: DscStampConfig): readonly DscToken[] {
  return config?.tokens?.length ? config.tokens : DSC_TOKENS;
}

/** Where the stamp goes on one document: the server's values over the built-in ones. */
export function dscPlacementFrom(
  config: DscStampConfig | undefined,
  document: SignableDocument
): DscStampPlacement {
  return { ...DSC_STAMP_PLACEMENT[document], ...config?.documents?.[document] };
}

/** The stamp's look and fixed lines: the server's values over the built-in ones. */
export function dscStampStyleFrom(config?: DscStampConfig): DscStampStyle {
  return {
    reason: DSC_STAMP_DETAILS.reason,
    location: DSC_STAMP_DETAILS.location,
    projectCode: DSC_STAMP_DETAILS.projectCode,
    ...config?.stamp,
  };
}
